import { NotificationBell } from 'src/components/notifications';
import PropTypes from 'prop-types';
// @mui
import { styled } from '@mui/material/styles';
import { Box, Stack, AppBar, Toolbar } from '@mui/material';
// hooks
import useOffSetTop from '../../../hooks/useOffSetTop';
import useResponsive from '../../../hooks/useResponsive';
// utils
import cssStyles from '../../../utils/cssStyles';
// config
import { HEADER, NAVBAR } from '../../../config';
import { homePathFor } from '../../../routes/paths';
// components
import Logo from '../../../components/Logo';
import Iconify from '../../../components/Iconify';
import { IconButtonAnimate } from '../../../components/animate';
//
// Search bar hidden!
import AccountPopover from './AccountPopover';
import LanguagePopover from './LanguagePopover';
import useAuth from 'src/hooks/useAuth';
import ToggleButton from 'src/components/settings/drawer/ToggleButton';
import useCollapseSettingDrawer from 'src/hooks/useCollapseSettingDrawer';

// ----------------------------------------------------------------------

const RootStyle = styled(AppBar, {
  shouldForwardProp: (prop) =>
    prop !== 'isCollapse' && prop !== 'isOffset' && prop !== 'verticalLayout'
})(({ isCollapse, isOffset, verticalLayout, theme }) => ({
  ...cssStyles(theme).bgBlur(),
  boxShadow: 'none',
  height: HEADER.MOBILE_HEIGHT,
  zIndex: theme.zIndex.appBar + 1,
  transition: theme.transitions.create(['width', 'height'], {
    duration: theme.transitions.duration.shorter
  }),
  [theme.breakpoints.up('lg')]: {
    height: HEADER.DASHBOARD_DESKTOP_HEIGHT,
    width: `calc(100% - ${NAVBAR.DASHBOARD_WIDTH + 1}px)`,
    ...(isCollapse && {
      width: `calc(100% - ${NAVBAR.DASHBOARD_COLLAPSE_WIDTH}px)`
    }),
    ...(isOffset && {
      height: HEADER.DASHBOARD_DESKTOP_OFFSET_HEIGHT
    }),
    ...(verticalLayout && {
      width: '100%',
      height: HEADER.DASHBOARD_DESKTOP_OFFSET_HEIGHT,
      backgroundColor: theme.palette.background.default
    })
  }
}));

// ----------------------------------------------------------------------

export default function DashboardHeader({
  onOpenSidebar,
  isCollapse = false,
  verticalLayout = false
}) {
  const isOffset =
    useOffSetTop(HEADER.DASHBOARD_DESKTOP_HEIGHT) && !verticalLayout;
  const { user, logout, type } = useAuth();
  const { collapseClickSetting, onToggleCollapseSetting } =
    useCollapseSettingDrawer();
  const isDesktop = useResponsive('up', 'lg');

  return (
    <RootStyle
      data-workspace-chrome
      isCollapse={isCollapse}
      isOffset={isOffset}
      verticalLayout={verticalLayout}
    >
      <Toolbar
        sx={{
          minHeight: '100% !important',
          px: { lg: 5 }
        }}
      >
        {/* The mark goes to this portal's own dashboard, never to the root
            portal chooser. */}
        {isDesktop && verticalLayout && (
          <Logo to={homePathFor(type)} sx={{ mr: 2.5 }} />
        )}

        {!isDesktop && (
          <IconButtonAnimate
            onClick={onOpenSidebar}
            sx={{ mr: 1, color: 'text.primary' }}
          >
            <Iconify icon="eva:menu-2-fill" />
          </IconButtonAnimate>
        )}

        <Box sx={{ flexGrow: 1 }} />

        <Stack
          direction="row"
          alignItems="center"
          spacing={{ xs: 0.5, sm: 1.5 }}
        >
          {user?.type === 'Client' && <LanguagePopover />}
          {(user?.type === 'user' || user?.type === 'Linguist') && (
            <ToggleButton
              open={collapseClickSetting}
              onToggle={onToggleCollapseSetting}
            />
          )}
          {user?.accountType === 'employee' && <NotificationBell key={user.id} />}
          <AccountPopover />
        </Stack>
      </Toolbar>
    </RootStyle>
  );
}
